import { PrismicRichText } from '@prismicio/react';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { unlessProp, whenProp } from 'remote-com-shared/style/helpers';
import styled, { css } from 'styled-components';

import { Title as TitleComponent, Section, BaseLinkStyle } from './ContentSlice';
import { Description, ColumnTitle as ColumnTitleComponent } from './TableSlice';
import { normaIcons } from './benefit-icons';
import TriangleIcon from './assets/triangle.svg';

import { fieldHasContent, transformProperties } from '@/src/domains/prismic/helpers';
import {
  desktopBreakpoint,
  fromTabletBreakpoint,
  tabletBreakpoint,
  untilLargeDesktopBreakpoint,
} from '@/src/domains/shared/breakpoints';

export const SectionWrapper = styled(Section)`
  ${ContentContainer} {
    max-width: 1100px;
  }

  ${Description} {
    display: flex;
    align-items: center;

    p {
      padding-bottom: 0;
    }

    svg {
      max-width: 30px;
      margin-right: 10px;
    }
  }
`;

export const Title = styled(TitleComponent)`
  && {
    ${({ theme }) => theme.typography.titleXXL}
    font-weight: 700;
    color: var(--colors-darkBlue);
  }
`;

export const ColumnTitle = styled(ColumnTitleComponent)`
  text-align: center;
`;

export const DescriptionWrapper = styled.div`
  padding: 30px 20px;
  height: 100%;
`;

export const MergedDescription = styled.div`
  display: none;
  padding: 30px 20px;

  ${whenProp(
    '$centered',
    css`
      ${fromTabletBreakpoint(css`
        justify-content: center;
        align-items: center;
      `)};
    `
  )}

  ${whenProp(
    '$addMarginTop',
    css`
      ${fromTabletBreakpoint(css`
        margin-top: 100px;

        ${whenProp(
          '$sixColumn',
          css`
            margin-top: 160px;
          `
        )}
      `)};
    `
  )}
`;

export const ColumnContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

export const Column = styled.td`
  color: rgba(20, 25, 30, 0.75);
  padding: 0;
  display: flex;
  flex-direction: column;
  position: relative;
  box-sizing: border-box;

  ${BaseLinkStyle}

  em {
    font-style: normal;
    ${({ theme }) => theme.typography.bodySpecial}
    font-weight: 700;
    color: var(--colors-irisBlue);
    margin-left: 5px;
  }

  &:not(:last-child) {
    ${ColumnContent} {
      ${fromTabletBreakpoint(css`
        border-right: 1px solid var(--colors-irisBlue);
      `)};
    }
  }

  &:first-child {
    h3 {
      ${({ theme }) => theme.typography.titleXL}
    }
  }

  ul {
    padding-left: 0;
    list-style: none;
    line-height: 1.7;
    margin-top: 0;
    margin-bottom: 5px;

    li {
      position: relative;
      margin-left: 5px;

      &::before {
        content: '-';
        color: var(--colors-irisBlue);
        margin-right: 5px;
        position: absolute;
        left: -12px;
      }
    }
  }

  ${tabletBreakpoint(css`
    border-top: 1px solid var(--colors-irisBlue);
    display: flex;
    flex-direction: row;
  `)};
`;

export const Table = styled.table`
  border-collapse: collapse;

  ${Column} {
    &:first-child {
      h3 {
        ${desktopBreakpoint(css`
          ${({ theme }) => theme.typography.titleMD};
        `)};
      }
    }
    ${({ $column }) => {
      switch ($column) {
        case false: {
          return css`
            width: 25%;

            &:nth-child(5) {
              display: none;
            }

            &:nth-child(4) {
              ${ColumnContent} {
                border-right: none;
              }
            }
          `;
        }
        case true:
        default:
          return css`
            width: 20%;
          `;
      }
    }}

    ${({ $sixColumn }) => {
      switch ($sixColumn) {
        case true: {
          return css`
            min-width: 16.5%;
            width: 16.5%;

            &:first-child {
              ${untilLargeDesktopBreakpoint(css`
                h3 {
                  ${({ theme }) => theme.typography.titleXS};
                }
                ${DescriptionWrapper} {
                  padding: 30px 7px;
                }
              `)};

              ${tabletBreakpoint(css`
                ${DescriptionWrapper} {
                  padding: 30px 20px;
                }
              `)};
            }
          `;
        }
        case false:
        default:
          return css`
            &:nth-child(6) {
              display: none;
            }

            &:nth-child(5) {
              ${ColumnContent} {
                border-right: none;
              }
            }
          `;
      }
    }}
  }
`;

export const MergedDescriptionStyle = css`
  display: flex;
  flex-direction: column;
  border-top: 1px solid var(--colors-irisBlue);
  position: relative;
  z-index: 1;
  background-color: var(--colors-blank);
  height: 100%;

  ${whenProp(
    '$removeExtraBorderTop',
    css`
      border-top: none;
    `
  )}

  ${({ $column }) => {
    switch ($column) {
      case true:
        return css`
          ${({ columnRange }) => {
            switch (columnRange) {
              case 1:
                return css`
                  width: 200%;
                `;
              case 2:
                return css`
                  width: 300%;
                `;
              case 3:
                return css`
                  width: 400%;
                `;
              case 4:
                return css`
                  width: 500%;
                `;
              default:
                return css`
                  width: 0%;
                `;
            }
          }};
        `;
      case false:
      default:
        return css`
          ${({ columnRange }) => {
            switch (columnRange) {
              case 1:
                return css`
                  width: 100%;
                `;
              case 2:
                return css`
                  width: 200%;
                `;
              case 3:
                return css`
                  width: 300%;
                `;
              default:
                return css`
                  width: 0%;
                `;
            }
          }};
        `;
    }
  }};
`;

export const TabletMergedDescriptionStyle = css`
  display: block;
  border-top: 1px solid var(--colors-irisBlue);

  ${whenProp(
    '$removeExtraBorderTop',
    css`
      border-top: none;
    `
  )}
`;

export const TableRow = styled.tr`
  display: flex;
  border-bottom: 1px solid var(--colors-irisBlue);

  ${tabletBreakpoint(css`
    flex-direction: column;

    ${Column} {
      width: 100%;
    }
  `)};

  ${whenProp(
    '$mergedDescription',
    css`
      ${Column} {
        ${fromTabletBreakpoint(css`
          &:nth-child(${({ startColumn }) => startColumn}) {
            ${MergedDescription} {
              ${MergedDescriptionStyle}
            }
          }
          &:nth-child(n + ${({ startColumn }) => startColumn + 1}):nth-child(
              -n + ${({ endColumn }) => endColumn}
            ) {
            ${MergedDescription} {
              display: none;
            }
          }
        `)};
        ${tabletBreakpoint(css`
          &:nth-child(n + ${({ startColumn }) => startColumn}):nth-child(
              -n + ${({ endColumn }) => endColumn}
            ) {
            ${MergedDescription} {
              ${TabletMergedDescriptionStyle}
            }
          }
        `)};
      }
    `
  )}

  ${whenProp(
    '$secondMergedDescription',
    css`
      ${Column} {
        ${fromTabletBreakpoint(css`
          &:nth-child(${({ secondStartColumn }) => secondStartColumn}) {
            ${MergedDescription} {
              ${MergedDescriptionStyle}
            }
          }
          &:nth-child(n + ${({ secondStartColumn }) => secondStartColumn + 1}):nth-child(
              -n + ${({ secondEndColumn }) => secondEndColumn}
            ) {
            ${MergedDescription} {
              display: none;
            }
          }
        `)};
        ${tabletBreakpoint(css`
          &:nth-child(n + ${({ secondStartColumn }) => secondStartColumn}):nth-child(
              -n + ${({ secondEndColumn }) => secondEndColumn}
            ) {
            ${MergedDescription} {
              ${TabletMergedDescriptionStyle}
            }
          }
        `)};
      }
    `
  )}
`;

const InfoIcon = styled(normaIcons.info)`
  width: 40px;
  height: 40px;
  color: var(--newBrandColor);
`;

export const IconWrapper = styled.div`
  position: absolute;
  bottom: -26px;
`;

export const TableTitleRow = styled(TableRow)`
  ${tabletBreakpoint(css`
    display: none;
  `)};

  ${whenProp(
    '$purple',
    css`
      background-color: rgba(20, 25, 30, 0.1);
      border-top-left-radius: 50px;
      border-top-right-radius: 50px;

      ${Column} {
        &:not(:last-child) {
          border-right: 1px solid var(--colors-irisBlue);
        }
      }
    `
  )}

  ${Column} {
    padding: 55px 20px 20px;
    align-items: center;
    text-align: center;

    ${whenProp(
      '$firstColEmpty',
      css`
        &:first-child {
          display: none;
        }

        &:nth-child(2) {
          width: ${({ column }) => (column ? '40' : '50')}%;

          ${IconWrapper} {
            left: 72%;
          }

          ${whenProp(
            '$sixColumn',
            css`
              width: ${({ column }) => (column ? '33' : '40')}%;
              min-width: 33%;
            `
          )}
        }

        &:nth-child(4) {
          border-right: ${({ column }) => !column && 'none'};
        }
      `
    )}

    ${unlessProp(
      '$sixColumn',
      css`
        &:nth-child(5) {
          border-right: none;
        }
      `
    )}
  }
`;

export const MobileColumnTitle = styled.div`
  display: none;

  ${tabletBreakpoint(css`
    display: block;
    color: var(--colors-irisBlue);
    font-weight: 700;
  `)};
`;

export const ColumnTitleDescription = styled.div`
  ${({ theme }) => theme.typography.bodySM};
`;

const MarketStandardWrapper = styled.div`
  max-width: 150px;

  ${fromTabletBreakpoint(css`
    margin-top: -38px;
  `)};
`;

const MobileColumnTitleWrapper = styled.div`
  padding: 20px 10px;
  width: 100%;
  justify-content: center;
  display: flex;
  flex-direction: column;
  border-right: 1px solid var(--colors-irisBlue);

  ${fromTabletBreakpoint(css`
    display: none;
  `)};
`;

const MarketStandardColumn = styled.div`
  --primaryColor: #14191e;

  ${({ theme }) => theme.typography.bodySM}
  font-weight: 700;
  text-transform: uppercase;
  color: var(--primaryColor);
  background-color: var(--colors-blank);
  border: 1px solid var(--primaryColor);
  border-radius: 50px;
  padding: 2px 7px;
  margin-bottom: 10px;
`;

export function BenefitsPlanTableSlice({ slice }) {
  const { primary, fields: table } = transformProperties(slice);

  const {
    sectionId,
    column,
    sixColumn,
    title,
    description,
    countryDescription,
    descriptionIcon,
    firstTitle,
    secondTitle,
    thirdTitle,
    fourthTitle,
    fifthTitle,
    sixthTitle,
    firstDescription,
    secondDescription,
    thirdDescription,
    fourthDescription,
    fifthDescription,
    sixthDescription,
    purpleTitle,
    marketStandardColumn,
    marketStandardLabel,
    firstSecondTitlesMerged,
  } = primary;

  const columnTitles = [firstTitle, secondTitle, thirdTitle, fourthTitle, fifthTitle, sixthTitle];
  const columnDescriptions = [
    firstDescription,
    secondDescription,
    thirdDescription,
    fourthDescription,
    fifthDescription,
    sixthDescription,
  ];

  return (
    <SectionWrapper id={sectionId}>
      <ContentContainer>
        <div>
          {fieldHasContent(title) && (
            <Title>
              <PrismicRichText field={title} />
            </Title>
          )}

          {fieldHasContent(description) && (
            <Description>
              {descriptionIcon && <InfoIcon />}
              {fieldHasContent(countryDescription) && (
                <PrismicRichText field={countryDescription} />
              )}
              <PrismicRichText field={description} />
            </Description>
          )}
        </div>
        <Table $column={column} $sixColumn={sixColumn}>
          <thead>
            <TableTitleRow
              column={column}
              $purple={purpleTitle}
              $firstColEmpty={firstSecondTitlesMerged}
              $sixColumn={sixColumn}
            >
              {columnTitles.map((columnTitle, index) => (
                <Column key={index}>
                  {marketStandardColumn === index + 1 && (
                    <MarketStandardWrapper>
                      <MarketStandardColumn>{marketStandardLabel}</MarketStandardColumn>
                    </MarketStandardWrapper>
                  )}
                  <ColumnTitle>{columnTitle}</ColumnTitle>
                  <ColumnTitleDescription>
                    <PrismicRichText field={columnDescriptions[index]} />
                  </ColumnTitleDescription>
                  {purpleTitle && (
                    <IconWrapper>
                      <TriangleIcon />
                    </IconWrapper>
                  )}
                </Column>
              ))}
            </TableTitleRow>
          </thead>
          <tbody>
            {table.map(
              (
                {
                  firstColumn,
                  secondColumn,
                  thirdColumn,
                  fourthColumn,
                  fifthColumn,
                  sixthColumn,
                  mergedDescription,
                  startColumn,
                  endColumn,
                  centered,
                  removeBorderTop,
                  addMarginTop,
                  secondMergedDescription,
                  secondStartColumn,
                  secondEndColumn,
                },
                index
              ) => {
                const descriptionsArray = [
                  firstColumn,
                  secondColumn,
                  thirdColumn,
                  fourthColumn,
                  fifthColumn,
                  sixthColumn,
                ];

                return (
                  <TableRow
                    key={index}
                    $mergedDescription={fieldHasContent(mergedDescription)}
                    startColumn={startColumn}
                    endColumn={endColumn}
                    columnRange={endColumn - startColumn}
                    $secondMergedDescription={fieldHasContent(secondMergedDescription)}
                    secondStartColumn={secondStartColumn}
                    secondEndColumn={secondEndColumn}
                    $removeExtraBorderTop={removeBorderTop} // when there is only merged description
                    $column={column}
                  >
                    {descriptionsArray.map((columnDescription, i) => (
                      <Column key={i}>
                        <MobileColumnTitleWrapper>
                          {marketStandardColumn === i + 1 && (
                            <MarketStandardWrapper>
                              <MarketStandardColumn>{marketStandardLabel}</MarketStandardColumn>
                            </MarketStandardWrapper>
                          )}

                          <div>
                            <MobileColumnTitle>{columnTitles[i]}</MobileColumnTitle>
                            <ColumnTitleDescription>
                              <PrismicRichText field={columnDescriptions[i]} />
                            </ColumnTitleDescription>
                          </div>
                        </MobileColumnTitleWrapper>

                        <ColumnContent>
                          {fieldHasContent(columnDescription) && (
                            <DescriptionWrapper>
                              <PrismicRichText field={columnDescription} />
                            </DescriptionWrapper>
                          )}
                          {fieldHasContent(mergedDescription) && (
                            <MergedDescription
                              $centered={centered}
                              $addMarginTop={addMarginTop}
                              $sixColumn={sixColumn}
                            >
                              <PrismicRichText field={mergedDescription} />
                            </MergedDescription>
                          )}
                        </ColumnContent>
                      </Column>
                    ))}
                  </TableRow>
                );
              }
            )}
          </tbody>
        </Table>
      </ContentContainer>
    </SectionWrapper>
  );
}
